
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import List from '@/common/components/UI/List/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// module components
import RolesListItem from '@/modules/GatewayModule/components/RolesListItem/index.vue'
import RolesActions from '@/modules/GatewayModule/components/RolesActions/index.vue'
import RolePermissionsList from '@/modules/GatewayModule/components/RolePermissionsList/index.vue'
import RolesFilters from '@/modules/GatewayModule/components/RolesFilters/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { gateway } from '@/core/realisations/gateway.realisation'

// types
import { CreateRolePayloadT, GatewayRoleT, RolesListParamsT } from '@/core/types/gateway.types'
import useActions from '@/common/composable/useActions'
import { RPPListParamsT } from '@/core/types/rpp.types'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        list: List,
        'failed-fetch': FailedFetch,
        'the-button': TheButton,
        'roles-list-item': RolesListItem,
        'roles-actions': RolesActions,
        'role-permissions-list': RolePermissionsList,
        'roles-filters': RolesFilters,
    },
    setup() {
        const state = reactive({
            roles: new DataState<GatewayRoleT>({}),
        })

        const filter_state: RolesListParamsT = reactive({
            name: undefined as string | undefined,
            permission: undefined as string | undefined,
        })

        const permissions_list_state = reactive({
            is_opened: false,
            role: null as GatewayRoleT | null,
        })

        const loadRoles = async (page: number) => {
            state.roles.pagination.current_page = page

            try {
                state.roles.is_loading = true

                const response = await gateway.getRoles(page, filter_state)
                state.roles.list = response.data
                state.roles.pagination.total_pages = response.meta.last_page

                state.roles.is_failed = false
                state.roles.failure.error_state.is_cancel = false
            } catch (error) {
                state.roles.failure.catchError(error)

                if (!state.roles.failure.error_state.is_cancel) {
                    state.roles.is_failed = true
                }
            } finally {
                if (!state.roles.failure.error_state.is_cancel) {
                    state.roles.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadRoles(1)
        })

        const openPermissionsList = (role: GatewayRoleT) => {
            permissions_list_state.role = role
            permissions_list_state.is_opened = true
        }

        const closePermissionsList = () => {
            permissions_list_state.is_opened = false
            permissions_list_state.role = null
        }

        const { actions_state, startCreate, startUpdate, startDelete } = useActions<GatewayRoleT>()

        const createRole = async (role: CreateRolePayloadT) => {
            try {
                actions_state.is_actions_loading = true
                await gateway.createRole(role)
                await loadRoles(state.roles.pagination.current_page)
                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updateRole = async (role: CreateRolePayloadT & { id: number }) => {
            try {
                actions_state.is_actions_loading = true
                await gateway.updateRole(role.id, role)
                await loadRoles(state.roles.pagination.current_page)
                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const deleteRole = async (role_id: number) => {
            try {
                actions_state.is_actions_loading = true
                await gateway.deleteRole(role_id)
                await loadRoles(state.roles.pagination.current_page)
                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const onSearch = (role: RolesListParamsT) => {
            filter_state.name = role.name
            filter_state.permission = role.permission

            loadRoles(1)
        }

        const onReset = () => {
            filter_state.name = undefined
            filter_state.permission = undefined

            loadRoles(1)
        }

        return {
            state,
            permissions_list_state,
            loadRoles,
            openPermissionsList,
            closePermissionsList,
            actions_state,
            startCreate,
            startUpdate,
            startDelete,
            createRole,
            updateRole,
            deleteRole,
            // filters
            onSearch,
            onReset,
        }
    },
})
